import { Parameter, FilterOptions, EventSchema, DataType, Priority, NonStandardField, UnifiedSchema } from '../types/schema';
import { v4 as uuidv4 } from 'uuid'; // Import uuid

/**
 * Extracts the last segment of a path
 */
export function getParameterNameFromPath(path: string): string {
  const parts = path.split('.');
  return parts[parts.length - 1];
}

/**
 * Helper to find parameter data type in schema
 */
export function findParameterDataTypeInSchema(schema: UnifiedSchema | null, path: string): string {
  if (!schema || !path) return ''; // Return empty string instead of undefined
  const param = schema.parameters.find(p => p.fullPath === path || p.path === path);
  return param?.dataTypes?.[0] || ''; // Return the first data type or empty string
}

/**
 * Returns a tailwind background color class based on data type
 */
export function getTypeColorClass(type?: string): string {
  switch (type) {
    case 'string':
      return 'bg-cyan-100 dark:bg-cyan-900 text-cyan-800 dark:text-cyan-200';
    case 'object':
      return 'bg-fuchsia-100 dark:bg-fuchsia-900 text-fuchsia-800 dark:text-fuchsia-200';
    case 'boolean':
      return 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-200';
    case 'number':
    case 'integer':
      return 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200';
    case 'array':
      return 'bg-rose-100 dark:bg-rose-900 text-rose-800 dark:text-rose-200';
    case 'null':
      return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';
    default:
      return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';
  }
}


/**
 * Determines if a parameter is global (present in 100% of events) or event-specific.
 */
export function determineParameterScope(
  param: Parameter,
  allEvents: EventSchema[]
): 'global' | 'eventSpecific' {
  if (!allEvents || allEvents.length === 0) {
    return 'eventSpecific'; // Default to event-specific if no events to compare against
  }

  const isGlobal = allEvents.every(event =>
    event.parameterFullPaths.some(eventParamFullPath => eventParamFullPath === (param.fullPath || ''))
  );

  return isGlobal ? 'global' : 'eventSpecific';
}

/**
 * Returns a tailwind border color class based on data type
 */
export function getTypeBorderClass(type: string, isDisabled: boolean = false): string {
  // Return gray border for disabled state regardless of type
  if (isDisabled) {
    return 'border-gray-300 dark:border-gray-700';
  }
  
  switch (type) {
    case 'string':
      return 'border-cyan-500 dark:border-cyan-600';
    case 'object':
      return 'border-fuchsia-500 dark:border-fuchsia-600';
    case 'boolean':
      return 'border-purple-500 dark:border-purple-600';
    case 'number':
    case 'integer':
      return 'border-amber-500 dark:border-amber-600';
    case 'array':
      return 'border-rose-500 dark:border-rose-600';
    case 'null':
      return 'border-gray-500 dark:border-gray-600';
    default:
      return 'border-gray-300 dark:border-gray-700';
  }
}

/**
 * Helper to check if a value is a plain object
 */
function isPlainObject(value: any): value is Record<string, any> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === null || proto === Object.prototype;
}

/**
 * Recursively extracts parameters from a JSON schema object or raw JSON data.
 * This function is designed to handle both JSON schema format (with properties, types, etc.)
 * and raw JSON data by inferring types.
 * It extracts field definitions while ignoring metadata like percentFound.
 */
export function extractSchemaFields(
  input: Record<string, any>,
  parentFullPath: string = "",
  parentPathSegments: string[] = [],
  allParameters: Parameter[] = [],
  nonStandardFieldsFound: NonStandardField[] = [], // New parameter to collect non-standard fields
  requiredFields: string[] = [] // New parameter to pass required fields from parent schema
): [Parameter[], NonStandardField[]] { // Changed return type
  if (!isPlainObject(input)) {
    return [allParameters, nonStandardFieldsFound];
  }

  // If this is the initial call (root level)
  if (!parentFullPath) {
    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        const value = input[key];

        // Filter out 'percentFound' and other non-standard metadata
        if (key === 'percentFound') {
          nonStandardFieldsFound.push({ field: key, path: parentFullPath || '' });
          continue;
        }

        // If the key is 'properties' and it's an object, recurse into it directly
        // This handles cases where the schema already has an explicit 'properties' wrapper
        // and we want to flatten it.
        if (key === 'properties' && isPlainObject(value)) {
          // Recurse into the 'properties' object, but treat its children as direct children of the current level (root)
          extractSchemaFields(value, parentFullPath, parentPathSegments, allParameters, nonStandardFieldsFound, requiredFields);
        } else {
          // For all other top-level keys, treat them as direct root parameters
          const currentPathSegments = [key]; // Direct child of root
          const currentPath = key;
          const currentFullPath = key;

          let dataTypes: DataType[] = [];
          let placeholder = '';
          let priorities: Priority[] = ['Standard'];
          let description = '';
          let friendlyName = toFriendlyName(key);
          let regexFormula = '';
          let llmInstructions = '';

          const isSchemaDefinition = isPlainObject(value) && (value.type || value.properties || value.items);

          if (isSchemaDefinition) {
            if (value.type) {
              if (Array.isArray(value.type)) {
                dataTypes = value.type.filter((t: string) =>
                  ['string', 'number', 'boolean', 'array', 'object', 'null'].includes(t)
                ) as DataType[];
              } else if (typeof value.type === 'string') {
                if (['string', 'number', 'boolean', 'array', 'object', 'null'].includes(value.type)) {
                  dataTypes = [value.type as DataType];
                }
              }
            } else if (value.properties && isPlainObject(value.properties)) {
              dataTypes = ['object'];
            } else if (value.items) {
              dataTypes = ['array'];
            } else if (isPlainObject(value)) {
              dataTypes = ['object'];
            } else {
              dataTypes = ['string'];
            }

            if (value.examples && value.examples.length > 0) {
              const example = value.examples[0];
              placeholder = typeof example === 'string'
                ? (example.length > 100 ? example.substring(0, 100) + '...' : example)
                : JSON.stringify(example);
            } else if (value.default !== undefined) {
              placeholder = typeof value.default === 'string'
                ? value.default
                : JSON.stringify(value.default);
            }
            // Determine priority based on the passed requiredFields array
            priorities = requiredFields.includes(key) ? ['Required'] : ['Standard'];
            description = value.description || '';
            friendlyName = value.title || toFriendlyName(key);
            regexFormula = value.pattern || '';
            llmInstructions = value.llmInstructions || '';

          } else {
            const actualType = typeof value;
            if (actualType === 'string') dataTypes = ['string'];
            else if (actualType === 'number') dataTypes = ['number'];
            else if (actualType === 'boolean') dataTypes = ['boolean'];
            else if (Array.isArray(value)) dataTypes = ['array'];
            else if (actualType === 'object' && value === null) dataTypes = ['null'];
            else if (isPlainObject(value)) dataTypes = ['object'];
            else dataTypes = ['string'];

            placeholder = typeof value === 'string'
              ? (value.length > 100 ? value.substring(0, 100) + '...' : String(value))
              : JSON.stringify(value);
          }

          const newParam: Parameter = {
            id: uuidv4(),
            path: currentPath,
            codeName: key,
            friendlyName: friendlyName,
            description: description,
            dataTypes: dataTypes,
            priorities: priorities,
            regexFormula: regexFormula,
            llmInstructions: llmInstructions,
            children: [],
            isExpanded: true,
            level: 0, // Root level
            isLastChild: false,
            parentPaths: [], // No parent paths for root
            scope: 'eventSpecific',
            placeholder: placeholder,
            fullPath: currentFullPath,
          };

          allParameters.push(newParam);

          // Recursively extract children for objects and arrays of objects
          if (isSchemaDefinition) {
            if (value.properties && isPlainObject(value.properties)) {
              extractSchemaFields(value.properties, currentFullPath, currentPathSegments, allParameters, nonStandardFieldsFound, value.required || []);
            } else if (value.items && isPlainObject(value.items) && value.items.properties && isPlainObject(value.items.properties)) {
              extractSchemaFields(value.items.properties, currentFullPath, currentPathSegments, allParameters, nonStandardFieldsFound, value.items.required || []);
            } else if (dataTypes.includes('object') && isPlainObject(value)) {
              extractSchemaFields(value, currentFullPath, currentPathSegments, allParameters, nonStandardFieldsFound, value.required || []);
            }
          } else {
            if (isPlainObject(value)) {
              extractSchemaFields(value, currentFullPath, currentPathSegments, allParameters, nonStandardFieldsFound, []);
            } else if (Array.isArray(value)) {
              value.forEach((item: any) => {
                if (isPlainObject(item)) {
                  extractSchemaFields(item, currentFullPath, currentPathSegments, allParameters, nonStandardFieldsFound, []);
                }
              });
            }
          }
        }
      }
    }
  } else {
    // Recursive calls (parentFullPath is not empty) - process normally
    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        const value = input[key];

        // Filter out 'percentFound' and other non-standard metadata
        if (key === 'percentFound') {
          nonStandardFieldsFound.push({ field: key, path: parentFullPath || '' });
          continue;
        }

        const currentPathSegments = [...parentPathSegments, key];
        const currentPath = currentPathSegments.join('.');
        const currentFullPath = parentFullPath ? `${parentFullPath}.${key}` : key;

        let dataTypes: DataType[] = [];
        let placeholder = '';
        let priorities: Priority[] = ['Standard'];
        let description = '';
        let friendlyName = toFriendlyName(key);
        let regexFormula = '';
        let llmInstructions = '';

        const isSchemaDefinition = isPlainObject(value) && (value.type || value.properties || value.items);

        if (isSchemaDefinition) {
          if (value.type) {
            if (Array.isArray(value.type)) {
              dataTypes = value.type.filter((t: string) =>
                ['string', 'number', 'boolean', 'array', 'object', 'null'].includes(t)
              ) as DataType[];
            } else if (typeof value.type === 'string') {
              if (['string', 'number', 'boolean', 'array', 'object', 'null'].includes(value.type)) {
                dataTypes = [value.type as DataType];
              }
            }
          } else if (value.properties && isPlainObject(value.properties)) {
            dataTypes = ['object'];
          } else if (value.items) {
            dataTypes = ['array'];
          } else if (isPlainObject(value)) {
            dataTypes = ['object'];
          } else {
            dataTypes = ['string'];
          }

          if (value.examples && value.examples.length > 0) {
            const example = value.examples[0];
            placeholder = typeof example === 'string'
              ? (example.length > 100 ? example.substring(0, 100) + '...' : example)
              : JSON.stringify(example);
          } else if (value.default !== undefined) {
            placeholder = typeof value.default === 'string'
              ? value.default
              : JSON.stringify(value.default);
          }
          // Determine priority based on the passed requiredFields array
          priorities = requiredFields.includes(key) ? ['Required'] : ['Standard'];
          description = value.description || '';
          friendlyName = value.title || toFriendlyName(key);
          regexFormula = value.pattern || '';
          llmInstructions = value.llmInstructions || '';

        } else {
          const actualType = typeof value;
          if (actualType === 'string') dataTypes = ['string'];
          else if (actualType === 'number') dataTypes = ['number'];
          else if (actualType === 'boolean') dataTypes = ['boolean'];
          else if (Array.isArray(value)) dataTypes = ['array'];
          else if (actualType === 'object' && value === null) dataTypes = ['null'];
          else if (isPlainObject(value)) dataTypes = ['object'];
          else dataTypes = ['string'];

          placeholder = typeof value === 'string'
            ? (value.length > 100 ? value.substring(0, 100) + '...' : String(value))
            : JSON.stringify(value);
        }

        const newParam: Parameter = {
          id: uuidv4(),
          path: currentPath,
          codeName: key,
          friendlyName: friendlyName,
          description: description,
          dataTypes: dataTypes,
          priorities: priorities,
          regexFormula: regexFormula,
          llmInstructions: llmInstructions,
          children: [],
          isExpanded: true,
          level: currentPathSegments.length - 1,
          isLastChild: false,
          parentPaths: parentPathSegments, // This is correct for nested
          scope: 'eventSpecific',
          placeholder: placeholder,
          fullPath: currentFullPath,
        };

        allParameters.push(newParam);

        // Recursively extract children for objects and arrays of objects
        if (isSchemaDefinition) {
          if (value.properties && isPlainObject(value.properties)) {
            extractSchemaFields(value.properties, currentFullPath, currentPathSegments, allParameters, nonStandardFieldsFound, value.required || []);
          } else if (value.items && isPlainObject(value.items) && value.items.properties && isPlainObject(value.items.properties)) {
            extractSchemaFields(value.items.properties, currentFullPath, currentPathSegments, allParameters, nonStandardFieldsFound, value.items.required || []);
          } else if (dataTypes.includes('object') && isPlainObject(value)) {
            extractSchemaFields(value, currentFullPath, currentPathSegments, allParameters, nonStandardFieldsFound, value.required || []);
          }
        } else {
          if (isPlainObject(value)) {
            extractSchemaFields(value, currentFullPath, currentPathSegments, allParameters, nonStandardFieldsFound, []);
          } else if (Array.isArray(value)) {
            value.forEach((item: any) => {
              if (isPlainObject(item)) {
                extractSchemaFields(item, currentFullPath, currentPathSegments, allParameters, nonStandardFieldsFound, []);
              }
            });
          }
        }
      }
    }
  }
  return [allParameters, nonStandardFieldsFound];
}

/**
 * Converts a string to a valid code name format (replaces spaces with underscores, preserves case)
 */
export function normalizeCodeName(str: string): string {
  if (!str) return '';
  
  return str
    .toLowerCase() // Convert to lowercase
    .trim()
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/[^a-z0-9_-]/g, '_') // Replace non-alphanumeric chars (except underscore and hyphen) with underscore
    .replace(/_+/g, '_') // Replace multiple underscores with a single one
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
}

/**
 * Converts a string to snake_case (e.g., "Friendly Name" to "friendly_name")
 */
export function toSnakeCase(str: string): string {
  if (!str) return '';
  return str
    .replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1_$2') // Add underscore before uppercase letters
    .replace(/[^a-zA-Z0-9_]+/g, '_') // Replace non-alphanumeric (except underscore) with underscore
    .replace(/_+/g, '_') // Replace multiple underscores with a single one
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
}

/**
 * Converts a snake_case string to a friendly name (e.g., "user_id" to "User ID")
 */
export const ABBREVIATION_MAP: { [key: string]: string } = {
  'url': 'URL',
  'ip': 'IP',
  'sku': 'SKU',
  'id': 'ID',
  'utm': 'UTM',
  'api': 'API',
  'sdk': 'SDK',
  'http': 'HTTP',
  'https': 'HTTPS',
  'ftp': 'FTP',
  'cdn': 'CDN',
  'ui': 'UI',
  'ux': 'UX',
  'geo': 'Geo',
  'lat': 'Latitude',
  'lon': 'Longitude',
  'os': 'OS',
  'dev': 'Dev',
  'prod': 'Prod',
  'qa': 'QA',
  'ab': 'A/B',
  'crm': 'CRM',
  'erp': 'ERP',
  'roi': 'ROI',
  'kpi': 'KPI',
  'dau': 'DAU',
  'mau': 'MAU',
  'ltv': 'LTV',
  'arpu': 'ARPU',
  'cpa': 'CPA',
  'cpc': 'CPC',
  'cpm': 'CPM',
  'ctr': 'CTR',
  'cv': 'CV',
  'cr': 'CR',
  'pv': 'PV',
  'uv': 'UV',
  'ga': 'GA',
  'gclid': 'GCLID',
  'fbclid': 'FBCLID',
  'cid': 'CID',
  'uid': 'UID',
  'tid': 'TID',
  'aid': 'AID',
  'sid': 'SID',
  'vid': 'VID',
  'ua': 'UA',
};

// Top 20 typical PII field names (case-insensitive)
export const PII_FIELD_NAMES: Set<string> = new Set([
  'email', 'phone', 'address', 'fullname', 'firstname', 'lastname', 'dob', 'ssn',
  'creditcard', 'bankaccount', 'passport', 'driverlicense', 'username', 'password',
  'ipaddress', 'geolocation', 'biometric', 'healthinfo', 'financialinfo', 'taxid'
]);

/**
 * Checks if a given field name is a typical PII field.
 */
export function isPiiField(fieldName: string): boolean {
  return PII_FIELD_NAMES.has(fieldName.toLowerCase());
}

export function toFriendlyName(codeName: string): string {
  if (!codeName) return '';

  // Check for exact abbreviation matches first (case-insensitive)
  const lowerCaseCodeName = codeName.toLowerCase();
  if (ABBREVIATION_MAP[lowerCaseCodeName]) {
    return ABBREVIATION_MAP[lowerCaseCodeName];
  }

  // Convert snake_case to "Friendly Name"
  // Convert camelCase to "Friendly Name"
  // Handle numbers and special characters gracefully
  return codeName
    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase to "camel Case"
    .replace(/_/g, ' ') // snake_case to "snake case"
    .replace(/-/g, ' ') // kebab-case to "kebab case"
    .split(' ')
    .map(word => {
      const lowerWord = word.toLowerCase();
      // Check for abbreviations within words, e.g., "image_url" -> "Image URL"
      if (ABBREVIATION_MAP[lowerWord]) {
        return ABBREVIATION_MAP[lowerWord];
      }
      // Capitalize first letter of each word
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

/**
 * Validates if a string is a valid code name (allows letters, numbers, underscores, hyphens, and spaces)
 */
export function isValidCodeName(str: string): boolean {
  if (!str) return true; // Allow empty string
  // Allows letters (upper and lower), numbers, underscores, hyphens, spaces, brackets, and parentheses
  return /^[a-zA-Z0-9 _$-()\[\]]+$/.test(str);
}

/**
 * Debounces a function with improved performance
 * This implementation uses a more efficient approach to reduce unnecessary renders
 */
export function debounce<T extends (...args: any[]) => void>(func: T, delay: number): T {
  let timeout: NodeJS.Timeout;
  let lastArgs: any[] | null = null;
  let lastCallTime = 0;
  
  // Function to execute the actual call
  const executeCall = () => {
    if (lastArgs) {
      func(...lastArgs);
      lastArgs = null;
    }
  };
  
  return ((...args: any[]) => {
    const now = Date.now();
    lastArgs = args;
    
    // If we're still within the delay period, clear the timeout and set a new one
    if (now - lastCallTime < delay) {
      clearTimeout(timeout);
      timeout = setTimeout(executeCall, delay);
    } else {
      // If we're outside the delay period, schedule the call
      lastCallTime = now;
      timeout = setTimeout(executeCall, delay);
    }
  }) as T;
}

/**
 * Filters parameters based on filter options
 */
export function filterParameters(parameters: Parameter[], options: FilterOptions): Parameter[] {
  return parameters.filter(param => {
    // Filter by search term
    const matchesSearch = 
      options.searchTerm === '' || 
      param.friendlyName.toLowerCase().includes(options.searchTerm.toLowerCase()) ||
      param.codeName.toLowerCase().includes(options.searchTerm.toLowerCase()) ||
      param.description.toLowerCase().includes(options.searchTerm.toLowerCase());
    
    // Filter by scope
    const matchesScope = 
      (param.scope === 'global' && options.showGlobal) || 
      (param.scope === 'eventSpecific' && options.showEventSpecific);
    
    // Filter by data type
    const matchesDataType = 
      !options.dataType || 
      param.dataTypes.includes(options.dataType);
    
    // Filter by priority
    const matchesPriority = 
      !options.priority || 
      param.priorities.includes(options.priority);
    
    return matchesSearch && matchesScope && matchesDataType && matchesPriority;
  });
}

/**
 * Generates a regex pattern based on a description
 * This is a placeholder for the AI-powered regex generation
 */
export function generateRegexFromDescription(description: string): string {
  // This would be replaced with an actual AI call in production
  if (!description) return '';
  
  if (description.toLowerCase().includes('email')) {
    return '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';
  }
  
  if (description.toLowerCase().includes('url')) {
    return '^(https?:\\/\\/)?([\\da-z.-]+)\\.([a-z.]{2,6})([\\/\\w .-]*)*\\/?$';
  }
  
  if (description.toLowerCase().includes('phone')) {
    return '^\\+?[1-9]\\d{1,14}$';
  }
  
  return '';
}

/**
 * Creates a new parameter with default values
 */
export function createNewParameter(
  scope: 'global' | 'eventSpecific',
  parentParam?: Parameter | null,
): Parameter {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 8);
  const id = `param-${timestamp}-${randomString}`;
  
  // Initialize with default values
  const defaultName = 'new_property';
  const initialFriendlyName = 'New Property';
  const initialCodeName = defaultName;

  // Calculate path based on parent
  let path: string;
  let fullPath: string;
  let parentPaths: string[] = [];
  let level = 0;
  
  if (parentParam && (parentParam.dataTypes.includes('object') || parentParam.dataTypes.includes('array'))) {
    // If parent exists and is an object or array
    const parentPathPrefix = parentParam.fullPath || '';
    path = parentPathPrefix ? `${parentPathPrefix}.${initialCodeName}` : initialCodeName;
    fullPath = path;
    level = (parentParam.level || 0) + 1;
    // Fix: parentPaths should contain the path segments, not full paths
    // For a parent with fullPath "context.consent.optOut", the parentPaths should be ["context", "consent", "optOut"]
    parentPaths = parentParam.fullPath ? parentParam.fullPath.split('.') : [];
  } else {
    // Root level parameter
    path = initialCodeName;
    fullPath = initialCodeName;
  }

  return {
    id,
    path,
    codeName: initialCodeName,
    friendlyName: initialFriendlyName,
    description: '',
    dataTypes: ['string'],
    priorities: ['Standard'],
    regexFormula: '',
    children: [],
    isExpanded: true,
    level,
    isLastChild: false,
    parentPaths,
    scope,
    placeholder: 'Friendly Name',
    fullPath
  };
}

/**
 * Builds a hierarchical tree structure from a flat list of parameters.
 * Parameters are nested under their parents using the 'children' property.
 */
export function buildParameterTree(flatParameters: Parameter[]): Parameter[] {
  const parameterMap = new Map<string, Parameter>();
  const rootParameters: Parameter[] = [];

  // First pass: Populate map and clone parameters to avoid modifying original objects
  flatParameters.forEach(param => {
    const clonedParam = { ...param, children: [] }; // Ensure children array is initialized
    parameterMap.set(clonedParam.fullPath, clonedParam);
  });

  // Second pass: Build the tree
  parameterMap.forEach(param => {
    if (param.parentPaths && param.parentPaths.length > 0) {
      const parentFullPath = param.parentPaths.join('.');
      const parent = parameterMap.get(parentFullPath);
      if (parent) {
        parent.children!.push(param);
      } else {
        // If a parent is not found (e.g., it's a top-level property that was not explicitly listed as root),
        // add it to rootParameters. This handles cases where the root of a path might not have an explicit
        // entry if it's implicitly defined by its children.
        rootParameters.push(param);
      }
    } else {
      rootParameters.push(param);
    }
  });

  // Sort children for consistent output (optional, but good for determinism)
  parameterMap.forEach(param => {
    if (param.children) {
      param.children.sort((a, b) => a.fullPath.localeCompare(b.fullPath));
    }
  });

  return flatParameters.filter(param => !param.parentPaths || param.parentPaths.length === 0)
                       .map(param => parameterMap.get(param.fullPath)!);
}

/**
 * Generates a natural language description for a transformation.
 */
/**
 * Generates a natural language description for a transformation, including Lua script if available.
 */
function generateTransformationDescription(transformation: string, inputKey: string, luaScript?: string, existingDescription?: string): string {
  let description = existingDescription || '';

  if (transformation && inputKey) {
    description += `${description ? '. ' : ''}Transformed using '${transformation}' from input key '${inputKey}'.`;
  } else if (transformation) {
    description += `${description ? '. ' : ''}Transformed using '${transformation}'.`;
  } else if (inputKey) {
    description += `${description ? '. ' : ''}Derived from input key '${inputKey}'.`;
  }

  if (luaScript) {
    description += `${description ? '. ' : ''}Generated using Lua script: \`${luaScript}\`.`;
  }

  return description.trim();
}

/**
 * Generates a regex pattern to match the input key.
 * This is a simple regex that matches the exact input key.
 */
function generateRegexFromInputKey(inputKey: string): string {
  if (!inputKey) return '';
  // Escape special regex characters in the inputKey
  const escapedInputKey = inputKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return `^${escapedInputKey}$`;
}

/**
 * Parses an OpenAPI 3.0.0 schema from a YAML object.
 * Extracts parameters from requestBody and requestHeaders, and events from paths/operations.
 */
function parseOpenApiSchema(openApiSpec: any): { events: EventSchema[], parameters: Parameter[] } {
  const allParameters: Parameter[] = [];
  const events: EventSchema[] = [];
  const nonStandardFields: NonStandardField[] = []; // To satisfy extractSchemaFields signature

  if (!openApiSpec || !openApiSpec.paths) {
    return { events: [], parameters: [] };
  }

  for (const pathKey in openApiSpec.paths) {
    if (Object.prototype.hasOwnProperty.call(openApiSpec.paths, pathKey)) {
      const pathItem = openApiSpec.paths[pathKey];

      for (const method in pathItem) {
        if (Object.prototype.hasOwnProperty.call(pathItem, method) && ['get', 'post', 'put', 'delete', 'patch'].includes(method)) {
          const operation = pathItem[method];
          const operationId = operation.operationId || `${method}_${pathKey.replace(/[^a-zA-Z0-9]/g, '_')}`;
          const eventName = toFriendlyName(operationId);
          const eventCodeName = normalizeCodeName(operationId);

          const newEvent: EventSchema = {
            id: uuidv4(),
            name: eventName,
            codeName: eventCodeName,
            description: operation.summary || operation.description || `Operation for ${method.toUpperCase()} ${pathKey}`,
            parameterFullPaths: [],
            isNew: true,
          };
          events.push(newEvent);

          // Process requestBody
          const requestBodySchema = operation.requestBody?.content?.['application/json']?.schema;
          if (requestBodySchema && requestBodySchema.properties) {
            const [bodyParams] = extractSchemaFields(
              requestBodySchema.properties,
              '', // Empty parentFullPath to treat as root level
              [], // Empty parentPathSegments to treat as root level
              [], // Start with empty parameters for this call
              nonStandardFields,
              requestBodySchema.required || []
            );
            bodyParams.forEach(param => {
              // Ensure unique parameters are added to allParameters
              if (!allParameters.some(p => p.fullPath === param.fullPath)) {
                allParameters.push({ ...param, scope: 'eventSpecific' }); // Mark as eventSpecific for now
              }
              newEvent.parameterFullPaths.push(param.fullPath);
            });
          } else if (requestBodySchema && requestBodySchema.items && requestBodySchema.items.properties) {
            // Handle array of objects in requestBody
            const [bodyParams] = extractSchemaFields(
              requestBodySchema.items.properties,
              '', // Empty parentFullPath to treat as root level
              [], // Empty parentPathSegments to treat as root level
              [], // Start with empty parameters for this call
              nonStandardFields,
              requestBodySchema.items.required || []
            );
            bodyParams.forEach(param => {
              if (!allParameters.some(p => p.fullPath === param.fullPath)) {
                allParameters.push({ ...param, scope: 'eventSpecific' });
              }
              newEvent.parameterFullPaths.push(param.fullPath);
            });
          }


          // Process requestHeaders
          const requestHeaders = operation.requestHeaders;
          if (requestHeaders && requestHeaders.properties) {
            const [headerParams] = extractSchemaFields(
              requestHeaders.properties,
              '', // Empty parentFullPath to treat as root level
              [], // Empty parentPathSegments to treat as root level
              [], // Start with empty parameters for this call
              nonStandardFields,
              requestHeaders.required || []
            );
            headerParams.forEach(param => {
              if (!allParameters.some(p => p.fullPath === param.fullPath)) {
                allParameters.push({ ...param, scope: 'eventSpecific' }); // Mark as eventSpecific for now
              }
              newEvent.parameterFullPaths.push(param.fullPath);
            });
          }

          // Process parameters defined at the operation level (query, header, path, cookie)
          if (operation.parameters && Array.isArray(operation.parameters)) {
            operation.parameters.forEach((param: any) => {
              const fullPath = `${param.in}.${param.name}`;
              const newParam: Parameter = {
                id: uuidv4(),
                fullPath: fullPath,
                path: param.name,
                codeName: param.name,
                friendlyName: toFriendlyName(param.name),
                description: param.description || '',
                dataTypes: [param.schema?.type || 'string'],
                priorities: param.required ? ['Required'] : ['Standard'],
                scope: 'eventSpecific', // Parameters at operation level are event-specific
                children: [],
                isExpanded: true,
                level: 0,
                parentPaths: [],
                isLastChild: false,
                placeholder: param.schema?.example !== undefined ? String(param.schema.example) : '',
                regexFormula: param.schema?.pattern || '',
                llmInstructions: '',
              };
              if (!allParameters.some(p => p.fullPath === newParam.fullPath)) {
                allParameters.push(newParam);
              }
              newEvent.parameterFullPaths.push(newParam.fullPath);
            });
          }
        }
      }
    }
  }

  return { events, parameters: allParameters };
}

/**
 * Parses a connection schema from a YAML object.
 * Extracts global parameters from the 'params' array.
 */
// function parseConnectionSchema(yamlObject: any): Parameter[] {
//   const parameters: Parameter[] = [];
//   if (yamlObject.params && Array.isArray(yamlObject.params)) {
//     yamlObject.params.forEach((param: any) => {
//       if (param.name) {
//         const newParam: Parameter = {
//           id: uuidv4(),
//           fullPath: param.name,
//           path: param.name,
//           codeName: param.name,
//           friendlyName: toFriendlyName(param.name),
//           description: param.description || '',
//           dataTypes: ['string'], // Connection parameters are typically strings
//           priorities: param.isOptional === false ? ['Required'] : ['Standard'],
//           scope: 'global', // Connection parameters are always global
//           children: [],
//           isExpanded: true,
//           level: 0,
//           parentPaths: [],
//           isLastChild: false,
//           placeholder: param.exampleValue !== undefined ? String(param.exampleValue) : (param.defaultValue !== undefined ? String(param.defaultValue) : ''),
//           regexFormula: '',
//           llmInstructions: '',
//         };
//         parameters.push(newParam);
//       }
//     });
//   }
//   return parameters;
// }

/**
 * Parses a connection.yaml file and extracts authentication parameters.
 * This function handles connection files that may contain both params and default/eventSpecific sections.
 */
export function parseConnectionYaml(yamlObject: any): Parameter[] {
  const parameters: Parameter[] = [];
  // No events are generated by connection YAML, only global parameters
  
  // Extract parameters from the params array
  if (yamlObject.params && Array.isArray(yamlObject.params)) {
    yamlObject.params.forEach((param: any) => {
      if (param.name) {
        const newParam: Parameter = {
          id: uuidv4(),
          fullPath: param.name,
          path: param.name,
          codeName: param.name,
          friendlyName: toFriendlyName(param.name),
          description: param.description || '',
          dataTypes: ['string'], // Connection parameters are typically strings
          priorities: param.isOptional === false ? ['Required'] : ['Standard'],
          scope: 'global', // Connection parameters are always global
          children: [],
          isExpanded: true,
          level: 0,
          parentPaths: [],
          isLastChild: false,
          placeholder: param.exampleValue !== undefined ? String(param.exampleValue) : (param.defaultValue !== undefined ? String(param.defaultValue) : ''),
          regexFormula: '',
          llmInstructions: '',
        };
        parameters.push(newParam);
      }
    });
  }
  
  // Extract authentication information from default section
  if (yamlObject.default) {
    // Extract basicAuth if present
    if (yamlObject.default.basicAuth) {
      const auth = yamlObject.default.basicAuth;
      if (auth.username) {
        // Check if the username is a reference to a parameter (e.g., $$USERNAME$$)
        const usernameMatch = auth.username.match(/\$\$(.*?)\$\$/);
        if (!usernameMatch || !parameters.some(p => p.codeName === usernameMatch[1])) {
          // Add as a parameter if it's not already referenced
          parameters.push({
            id: uuidv4(),
            fullPath: 'basicAuth.username',
            path: 'basicAuth.username',
            codeName: 'username',
            friendlyName: 'Username',
            description: 'Basic authentication username',
            dataTypes: ['string'],
            priorities: ['Required'],
            scope: 'global',
            children: [],
            isExpanded: true,
            level: 0,
            parentPaths: [],
            isLastChild: false,
            placeholder: auth.username,
            regexFormula: '',
            llmInstructions: '',
          });
        }
      }
      
      if (auth.password) {
        // Check if the password is a reference to a parameter (e.g., $$PASSWORD$$)
        const passwordMatch = auth.password.match(/\$\$(.*?)\$\$/);
        if (!passwordMatch || !parameters.some(p => p.codeName === passwordMatch[1])) {
          // Add as a parameter if it's not already referenced
          parameters.push({
            id: uuidv4(),
            fullPath: 'basicAuth.password',
            path: 'basicAuth.password',
            codeName: 'password',
            friendlyName: 'Password',
            description: 'Basic authentication password',
            dataTypes: ['string'],
            priorities: ['Required'],
            scope: 'global',
            children: [],
            isExpanded: true,
            level: 0,
            parentPaths: [],
            isLastChild: false,
            placeholder: auth.password,
            regexFormula: '',
            llmInstructions: '',
          });
        }
      }
    }
    
    // Extract API key from transform if present
    if (yamlObject.default.transform && yamlObject.default.transform.body) {
      const transformBody = yamlObject.default.transform.body;
      // Look for API key references in the transform body
      const apiKeyMatches = transformBody.match(/api_key\s*=\s*['"]?\$\$(.*?)\$\$['"]?/g);
      if (apiKeyMatches) {
        apiKeyMatches.forEach((match: string) => {
          const keyMatch = match.match(/\$\$(.*?)\$\$/);
          if (keyMatch && !parameters.some(p => p.codeName === keyMatch[1])) {
            // Add as a parameter if it's not already in the parameters list
            parameters.push({
              id: uuidv4(),
              fullPath: keyMatch[1],
              path: keyMatch[1],
              codeName: keyMatch[1],
              friendlyName: toFriendlyName(keyMatch[1]),
              description: 'API Key extracted from transform',
              dataTypes: ['string'],
              priorities: ['Required'],
              scope: 'global',
              children: [],
              isExpanded: true,
              level: 0,
              parentPaths: [],
              isLastChild: false,
              placeholder: '',
              regexFormula: '',
              llmInstructions: '',
            });
          }
        });
      }
    }
    
    // Extract endpoint information
    if (yamlObject.default.endpoint) {
      const endpoint = yamlObject.default.endpoint;
      
      // Create a default endpoint parameter even if there are no variable references
      if (!parameters.some(p => p.codeName === 'ENDPOINT')) {
        parameters.push({
          id: uuidv4(),
          fullPath: 'ENDPOINT',
          path: 'ENDPOINT',
          codeName: 'ENDPOINT',
          friendlyName: 'Endpoint URL',
          description: 'API endpoint URL',
          dataTypes: ['string'],
          priorities: ['Required'],
          scope: 'global',
          children: [],
          isExpanded: true,
          level: 0,
          parentPaths: [],
          isLastChild: false,
          placeholder: endpoint,
          regexFormula: '',
          llmInstructions: '',
        });
      }
      
      // Look for parameter references in the endpoint URL
      const endpointMatches = endpoint.match(/\$\$(.*?)\$\$/g);
      if (endpointMatches) {
        endpointMatches.forEach((match: string) => {
          const paramMatch = match.match(/\$\$(.*?)\$\$/);
          if (paramMatch && !parameters.some(p => p.codeName === paramMatch[1])) {
            // Add as a parameter if it's not already in the parameters list
            parameters.push({
              id: uuidv4(),
              fullPath: paramMatch[1],
              path: paramMatch[1],
              codeName: paramMatch[1],
              friendlyName: toFriendlyName(paramMatch[1]),
              description: 'Endpoint parameter',
              dataTypes: ['string'],
              priorities: ['Required'],
              scope: 'global',
              children: [],
              isExpanded: true,
              level: 0,
              parentPaths: [],
              isLastChild: false,
              placeholder: '',
              regexFormula: '',
              llmInstructions: '',
            });
          }
        });
      }
    }
    
    // Extract header parameters
    if (yamlObject.default.headers && Array.isArray(yamlObject.default.headers)) {
      yamlObject.default.headers.forEach((header: any) => {
        if (header.fromEventKey) {
          // This is a dynamic header that gets its value from an event property
          parameters.push({
            id: uuidv4(),
            fullPath: `header.${header.key}`,
            path: `header.${header.key}`,
            codeName: header.key,
            friendlyName: toFriendlyName(header.key),
            description: `Header mapped from event property: ${header.fromEventKey}`,
            dataTypes: ['string'],
            priorities: ['Standard'],
            scope: 'global',
            children: [],
            isExpanded: true,
            level: 0,
            parentPaths: [],
            isLastChild: false,
            placeholder: header.fromEventKey,
            regexFormula: '',
            llmInstructions: '',
          });
        }
      });
    }
  }
  
  return parameters;
}

/**
* Parses a vendor schema from a YAML object.
* Extracts parameters from enrichments and mappings, and events from eventSpecific keys.
*/
export function parseVendorSchemaFromYaml(yamlObject: any): { events: EventSchema[], parameters: Parameter[] } {
  // Check if it's an OpenAPI schema
  if (yamlObject.openapi && yamlObject.paths) {
    return parseOpenApiSchema(yamlObject);
  }


  // Existing logic for vendor-specific schema
  const events: EventSchema[] = [];
  const paramMap = new Map<string, Parameter>();

  // Process params section if it exists (for global parameters)
  if (yamlObject.params && Array.isArray(yamlObject.params)) {
    yamlObject.params.forEach((param: any) => {
      if (param.name && !paramMap.has(param.name)) {
        const newParam: Parameter = {
          id: uuidv4(),
          fullPath: param.name,
          path: param.name,
          codeName: param.name,
          friendlyName: toFriendlyName(param.name),
          description: param.description || '',
          dataTypes: ['string'], // Params are typically strings
          priorities: param.isOptional === false ? ['Required'] : ['Standard'],
          scope: 'global',
          children: [],
          isExpanded: true,
          level: 0,
          parentPaths: [],
          isLastChild: false,
          placeholder: param.exampleValue !== undefined ? String(param.exampleValue) : (param.defaultValue !== undefined ? String(param.defaultValue) : ''),
          regexFormula: '',
          llmInstructions: '',
        };
        paramMap.set(param.name, newParam);
      }
    });
  }

  const processSection = (section: any, scope: 'global' | 'eventSpecific', eventName?: string) => {
    if (!section) return;

    const processItems = (items: any[]) => {
      if (!Array.isArray(items)) return;
      items.forEach(item => {
        if (item.outputKey) {
          // Process the output key, handling nested paths
          const outputKeyParts = item.outputKey.split('.');
          
          // Create or update parameters for each part of the path
          let currentPath = '';
          let parentPath = '';
          let parentPaths: string[] = [];

          // Extract Lua script from transforms if available
          let luaScript: string | undefined;
          if (Array.isArray(item.transforms)) {
            for (const transform of item.transforms) {
              if (transform.expression && transform.expression.lang === 'lua' && transform.expression.body) {
                luaScript = transform.expression.body;
                break;
              }
            }
          }
          
          outputKeyParts.forEach((part: string, index: number) => {
            // Build the current path
            currentPath = currentPath ? `${currentPath}.${part}` : part;
            
            // Skip creating the parameter if it already exists
            if (!paramMap.has(currentPath)) {
              // Determine if this is a leaf node (the actual output key)
              const isLeafNode = index === outputKeyParts.length - 1;
              
              // For nested paths, we need to ensure the parent paths are correctly set
              let paramParentPaths: string[] = [];
              if (index > 0) {
                // For nested paths, add the parent path
                const parentPathForParam = outputKeyParts.slice(0, index).join('.');
                paramParentPaths = [parentPathForParam];
              }
              
              const newParam: Parameter = {
                id: uuidv4(),
                fullPath: currentPath,
                path: currentPath,
                codeName: part,
                friendlyName: toFriendlyName(part),
                description: isLeafNode ? generateTransformationDescription(item.transformation, item.inputKey, luaScript, item.description) : '',
                dataTypes: isLeafNode ? ['string'] : ['object'], // Assign 'object' if it's a parent, else default to 'string'
                priorities: ['Standard'],
                scope,
                children: [],
                isExpanded: true,
                level: index,
                parentPaths: paramParentPaths,
                isLastChild: false,
                placeholder: '',
                regexFormula: luaScript ? '' : generateRegexFromInputKey(item.inputKey), // Do not add regex if Lua is present
                llmInstructions: '',
              };
              
              paramMap.set(currentPath, newParam);
            }
            
            // Update parent paths for the next iteration
            if (parentPath) {
              parentPaths.push(parentPath);
            }
            parentPath = currentPath;
          });
          
          // Add the output key to the event's parameter paths
          if (eventName) {
            const event = events.find(e => e.codeName === eventName);
            if (event) {
              // Add the full output key path
              if (!event.parameterFullPaths.includes(item.outputKey)) {
                event.parameterFullPaths.push(item.outputKey);
              }
              
              // For nested paths, also add each segment of the path
              outputKeyParts.forEach((_: string, index: number) => {
                const pathSegment = outputKeyParts.slice(0, index + 1).join('.');
                if (!event.parameterFullPaths.includes(pathSegment)) {
                  event.parameterFullPaths.push(pathSegment);
                }
              });
            }
          }
        }
      });
    };

    processItems(section.enrichments || []);
    processItems(section.mappings || []);
  };

  // Process default section if it exists
  if (yamlObject.default) {
    processSection(yamlObject.default, 'global');
  }

  // Process global sections
  if (yamlObject.global) {
    processSection(yamlObject.global, 'global');
  }

  // Process eventSpecific sections
  if (yamlObject.eventSpecific) {
    for (const eventName in yamlObject.eventSpecific) {
      const newEvent: EventSchema = {
        id: uuidv4(),
        name: toFriendlyName(eventName),
        codeName: normalizeCodeName(eventName),
        description: `Schema for ${eventName}`,
        parameterFullPaths: [],
        isNew: true,
      };
      events.push(newEvent);
      processSection(yamlObject.eventSpecific[eventName], 'eventSpecific', eventName);
    }
  }

  // Build parent-child relationships
  const parameters = Array.from(paramMap.values());
  parameters.forEach(param => {
    if (param.parentPaths && param.parentPaths.length > 0) {
      const parentPath = param.parentPaths[param.parentPaths.length - 1];
      const parentParam = paramMap.get(parentPath);
      if (parentParam) {
        // Ensure children array is initialized
        if (!parentParam.children) {
          parentParam.children = [];
        }
        // Add this parameter as a child of its parent
        if (!parentParam.children.some(child => child.fullPath === param.fullPath)) {
          parentParam.children.push(param);
        }
      }
    }
  });

  // Add global params to all events
  const globalParams = parameters.filter(p => p.scope === 'global');
  events.forEach(event => {
    globalParams.forEach(p => {
      if (!event.parameterFullPaths.includes(p.fullPath)) {
        event.parameterFullPaths.push(p.fullPath);
      }
    });
  });

  return { events, parameters };
}
